import { t } from "i18next";

import type { ColumnsClarFactory } from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { formatDateWithFallback } from "@/core/utils/func/formatDateWithFallback";
import type { ApprovalRequestDto } from "@/features/admin/api/dto";
import { ApprovalRequestRowActions } from "@/features/admin/components/approval-requests/approval-request-row-actions";
import { ApprovalRequestStatusBadge } from "@/features/admin/components/approval-requests/approval-request-status-badge";
import {
  APPROVAL_ACTION_TYPES,
  APPROVAL_STATUSES,
} from "@/features/shared/api/enums";

/** Ligne de la file d'approbations : `DataTableClar` exige un `id` requis. */
export type ApprovalRequestRow = ApprovalRequestDto & { id: string };

const selectOptions = (values: readonly string[]) =>
  values.map((value) => ({ value, label: value }));

/**
 * Colonnes de la file d'approbations (`POST /ApprovalRequests/all`).
 * Filtres select sur `actionType` / `status` et texte sur `sourceService`
 * (`ApprovalRequestFilter`). Identifiants et motifs en `NoTranslate`.
 */
export const approvalRequestsColumns: ColumnsClarFactory<ApprovalRequestRow> = (
  navigate
) => [
  {
    id: "actionType",
    accessorKey: "actionType",
    header: t("admin.approvals.columns.actionType"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">
        <Badge variant="secondary">{getValue() as string}</Badge>
      </NoTranslate>
    ),
    meta: { type: "select", options: selectOptions(APPROVAL_ACTION_TYPES) },
  },
  {
    id: "sourceService",
    accessorKey: "sourceService",
    header: t("admin.approvals.columns.sourceService"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-mono text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "text" },
  },
  {
    id: "resource",
    accessorFn: (row) => `${row.resourceType} ${row.resourceId}`,
    header: t("admin.approvals.columns.resource"),
    cell: ({ row }) => (
      <NoTranslate as="span" className="font-mono text-xs">
        {row.original.resourceType} · {row.original.resourceId.slice(0, 8)}…
      </NoTranslate>
    ),
    // Colonne composite : resourceType/resourceId sont dans l'API mais
    // l'id de colonne n'y correspond pas — pas de filtre cosmétique.
    meta: { type: "denied" },
  },
  {
    id: "reason",
    accessorKey: "reason",
    header: t("admin.approvals.columns.reason"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="line-clamp-1 text-sm">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    // Pas dans ApprovalRequestFilter.
    meta: { type: "denied" },
  },
  {
    id: "status",
    accessorKey: "status",
    header: t("admin.approvals.columns.status"),
    cell: ({ row }) => (
      <ApprovalRequestStatusBadge status={row.original.status} />
    ),
    meta: { type: "select", options: selectOptions(APPROVAL_STATUSES) },
  },
  {
    id: "requestedByKeycloakId",
    accessorKey: "requestedByKeycloakId",
    header: t("admin.approvals.columns.requestedBy"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-mono text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    // Pas dans ApprovalRequestFilter.
    meta: { type: "denied" },
  },
  {
    id: "requestedAt",
    accessorKey: "requestedAt",
    header: t("admin.approvals.columns.requestedAt"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="text-xs">
        {formatDateWithFallback(getValue() as string | null, "—")}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "actions",
    header: t("common.actions"),
    enableHiding: false,
    cell: ({ row }) =>
      row.original.id ? (
        <ApprovalRequestRowActions
          approvalRequest={row.original}
          navigate={navigate}
        />
      ) : null,
    meta: { type: "denied" },
  },
];
